import { useEffect, useMemo, useRef, useState } from "react";
import { resolveImageGeneratorResultSrc } from "../../../app/api/imageGenerator";

type PreviewItem = {
  id: string;
  imageUrl: string | null;
};

/**
 * Shared blob/auth preview resolver for IG and Collection Build result tiles.
 * Resolves relative/auth-gated urls one-at-a-time so parallel 401 refresh
 * stampedes do not leave only the first tile with a working blob.
 */
const useGenerativeResultPreviews = (items: PreviewItem[]) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const cacheRef = useRef<Map<string, string>>(new Map());
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const signature = useMemo(
    () => items.map(item => `${item.id}:${item.imageUrl || ""}`).join("|"),
    [items]
  );

  useEffect(() => {
    let cancelled = false;
    const cache = cacheRef.current;
    const currentItems = itemsRef.current;
    const needed = new Set(
      currentItems
        .map(item => item.imageUrl)
        .filter((url): url is string => Boolean(url))
    );

    const resolveMissing = async (urls: string[]) => {
      // Sequential: parallel admin.get + interceptor refresh races often leave
      // only the first auth-gated preference preview populated.
      for (const url of urls) {
        if (cancelled || cache.has(url)) {
          continue;
        }
        try {
          cache.set(url, await resolveImageGeneratorResultSrc(url));
        } catch {
          // Leave uncached so a later signature/effect pass can retry.
        }
      }
    };

    const previewsFor = (list: PreviewItem[]) => {
      const next: Record<string, string> = {};
      for (const item of list) {
        if (item.imageUrl && cache.has(item.imageUrl)) {
          next[item.id] = cache.get(item.imageUrl) as string;
        }
      }
      return next;
    };

    (async () => {
      const missing = Array.from(needed).filter(url => !cache.has(url));
      await resolveMissing(missing);
      if (cancelled) {
        return;
      }

      Array.from(cache.entries()).forEach(([url, blobUrl]) => {
        if (!needed.has(url)) {
          // Only revoke blobs this hook created from relative/auth URLs.
          // Passthrough blob:/data:/http(s) inputs share url===blobUrl — revoking
          // those breaks sibling tiles that still point at the same object URL.
          if (blobUrl.startsWith("blob:") && blobUrl !== url) {
            URL.revokeObjectURL(blobUrl);
          }
          cache.delete(url);
        }
      });

      setPreviews(previewsFor(currentItems));

      // Retry once if auth-gated relative urls failed on the first pass.
      const stillMissing = Array.from(needed).filter(url => !cache.has(url));
      if (!stillMissing.length) {
        return;
      }
      await new Promise(resolve => window.setTimeout(resolve, 400));
      if (cancelled) {
        return;
      }
      await resolveMissing(stillMissing);
      if (cancelled) {
        return;
      }
      setPreviews(previewsFor(itemsRef.current));
    })();

    return () => {
      cancelled = true;
    };
  }, [signature]);

  useEffect(
    () => () => {
      cacheRef.current.forEach((blobUrl, url) => {
        if (blobUrl.startsWith("blob:") && blobUrl !== url) {
          URL.revokeObjectURL(blobUrl);
        }
      });
      cacheRef.current.clear();
    },
    []
  );

  return previews;
};

export default useGenerativeResultPreviews;
